import { useTranslation } from "react-i18next";
import { useInfoModal } from "../Context/InfoModalProvider";
import { useRef } from "react";
//import Compressor from "compressorjs";
import { PictureIcon } from "./Icons/PictureIcon";
import { CameraIcon } from "./Icons/CameraIcon";
import { CloseIcon } from "./Icons/CloseIcon";
import { ChangeIcon } from "./Icons/ChangeIcon";

interface ImageInputProps {
  images: File[];
  setImages: CallableFunction;
  title?: string | null;
  multiple?: boolean;
  required?: boolean;
  preview?: boolean;
  infoText? : string | null;
  className? : string | null;
}

const ImageInput: React.FunctionComponent<ImageInputProps> = ({
  images,
  setImages,
  title,
  multiple = false,
  required = true,
  preview = true,
  infoText,
  className = ''
}) => {
  const { t } = useTranslation();
  const { showInfo } = useInfoModal();

  const idRef = useRef<string>(Math.random().toString(36).substring(2));
  const inputRef = useRef<HTMLInputElement | null>(null);

  const removeImage = (index: number) => {
    let newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
    if (newImages.length === 0 && inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const imageOnchange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);

      const smallFiles = filesArray.filter((x) => x.size / 1000 / 1000 <= 40);
      if (filesArray.length > smallFiles.length) {
        showInfo(t("Please select images smaller than 10MB!"));
      }

      filesArray.forEach((file) => {
        const reader = new FileReader();
        const img = new Image();

        reader.onload = (event) => {
          img.src = event.target?.result as string;
        };

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const maxWidth = 1200;
          const maxHeight = 1200;
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            const scale = Math.min(maxWidth / width, maxHeight / height);
            width = width * scale;
            height = height * scale;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");

          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                  if (blob) {
                    const webpFile = new File(
                        [blob],
                        file.name.replace(/\.\w+$/, ".webp"),
                        { type: "image/webp" }
                    );
                    setImages((prev: File[]) => [...prev, webpFile]);
                  }
                },
                "image/webp",
                0.8 // quality
            );
          }
        };

        reader.onerror = (err) => {
          console.error("WebP conversion failed", err);
          setImages((prev: File[]) => [...prev, file]);
        };

        reader.readAsDataURL(file);
      });
    }
  };


  return (
      <div className={"image-input " + className + (preview && images.length ? " has-image" : "")}>
        <div className="flex gap-2 items-center">

          {images.length === 0 && (
              <>
                <div className="image-input__nopic">
                  <PictureIcon />
                </div>
              </>
          )}

          {preview && images.length === 1 ? (
              <>
                {Array.from(images)?.map((image, index) => (
                    <>
                      <div
                          className="picture-preview"
                          key={index}
                          onClick={() => removeImage(index)}
                      >
                        <button className="picture-preview__delete" type="button">
                          <CloseIcon />
                        </button>
                        <div
                            className="picture-preview__picture"
                            style={{
                              backgroundImage: `url(${URL.createObjectURL(image)})`,
                            }}
                        >
                          {/*}
                  <img
                    className="mt-4 mb-3"
                    src={URL.createObjectURL(image)}
                    alt="Selected file"
                  />*/}
                        </div>
                      </div>

                    </>
                ))}
              </>
          ) : null}

          <div className="image-input__info">{infoText}</div>

          <input
              type="file"
              id={idRef.current}
              ref={inputRef}
              className="file-input"
              multiple={multiple}
              accept="image/*"
              onChange={imageOnchange}
              required={required && images.length === 0}
          />
          <label htmlFor={idRef.current} style={{marginLeft: 'auto'}}>
            {(multiple || images.length === 0) ? (
                <>
                  <CameraIcon />
                  {title ?? t("Take a photo")}
                </>
            ) : (
                <>
                  <ChangeIcon />
                  {title ?? t("Retake")}
                </>
            )}
          </label>
        </div>

        {preview && images.length > 1 ? (
            <div className="mt-4 flex gap-2 flex-wrap">
              {Array.from(images)?.map((image, index) => (
                  <div
                      className="picture-preview"
                      key={index}
                      onClick={() => removeImage(index)}
                  >
                    <button className="picture-preview__delete" type="button">
                      <CloseIcon />
                    </button>
                    <div
                        className="picture-preview__picture"
                        style={{
                          backgroundImage: `url(${URL.createObjectURL(image)})`,
                        }}
                    >
                      {/*}
                <img
                  className="mt-4 mb-3"
                  src={URL.createObjectURL(image)}
                  alt="Selected file"
                />*/}
                    </div>
                  </div>
              ))}
            </div>
        ) : null}

      </div>
  );
};

export default ImageInput;
